import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Property } from "../../data/models";
import PropertyCard from "../valuation/PropertyCard";
import StatsCard from "../StatsCard";
import { useNavigate, useParams } from "react-router-dom";

// Define interface for stat object
interface Stat {
  title: string;
  value: string;
  change: string;
  trend: "up" | "down" | "neutral";
  icon: {
    path: string;
    bgColor: string;
  };
}

// Define interface for component props

const DataCollectorAssignments = () => {
  const { id: propertyId } = useParams();
  const { currentUser, token } = useAuth();
  const [filter, setFilter] = useState<
    "all" | "assigned" | "inProgress" | "completed"
  >("all");
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError(null);

      // Assuming authentication is required; adjust if currentUser.token exists or use another auth mechanism
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(
        "https://valuate-api.vercel.app/properties",
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: any = await response.json();
      setProperties(data.data);
    } catch (err) {
      console.error("Error fetching properties:", err);
      setError("Failed to load properties. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchProperties();
  }, []);

  // Filter properties for Data collector
  const getDataCollectorProperties = (): Property[] => {
    let filteredProperties: Property[] = properties;

    // Apply filters
    switch (filter) {
      case "assigned":
        filteredProperties = properties.filter(
          (p) => !p.status || p.status === "New"
        );
        break;
      case "inProgress":
        filteredProperties = properties.filter(
          (p) => p.status === "In Progress"
        );
        break;
      case "completed":
        filteredProperties = properties.filter(
          (p) => p.valuationDetails && p.valuationDetails.status === "Completed"
        );
        break;
      default:
        // 'all' - no additional filtering
        break;
    }

    return filteredProperties;
  };

  const filteredProperties: Property[] = getDataCollectorProperties();

  const navigate = useNavigate();

  const onPropertySelect = (propertyId: string) => {
    navigate(`/data-dashboard/property/valuation/${propertyId}`);
  };
  // Stats for Data collector
  const stats: Stat[] = [
    {
      title: "Assigned Properties",
      value: properties
        .filter((p) => !p.status || p.status === "New")
        .length.toString(),
      change: "New tasks",
      trend: "neutral",
      icon: {
        path: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
        bgColor: "bg-gray-500"
      }
    },
    {
      title: "In Progress",
      value: properties
        .filter((p) => p.status === "In Progress")
        .length.toString(),
      change: "Active collection",
      trend: "neutral",
      icon: {
        path: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
        bgColor: "bg-gray-500"
      }
    },
    {
      title: "Completed",
      value: properties
        .filter(
          (p) => p.valuationDetails && p.valuationDetails.status === "Completed"
        )
        .length.toString(),
      change: "Ready for review",
      trend: "up",
      icon: {
        path: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
        bgColor: "bg-gray-500"
      }
    },
    {
      title: "Efficiency Rate",
      value: "92%",
      change: "+5% this month",
      trend: "up",
      icon: {
        path: "M13 10V3L4 14h7v7l9-11h-7z",
        bgColor: "bg-gray-500"
      }
    }
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-gray-800">
        Data Collector Assignments
      </h1>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <div className="ml-auto flex bg-white rounded-md shadow-sm">
          <button
            onClick={() => setFilter("assigned")}
            className={`px-4 py-2 text-sm font-medium ${
              filter === "assigned"
                ? "bg-indigo-50 text-indigo-700"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            Assigned
          </button>
          <button
            onClick={() => setFilter("inProgress")}
            className={`px-4 py-2 text-sm font-medium ${
              filter === "inProgress"
                ? "bg-indigo-50 text-indigo-700"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            In Progress
          </button>
          <button
            onClick={() => setFilter("completed")}
            className={`px-4 py-2 text-sm font-medium rounded-r-md ${
              filter === "completed"
                ? "bg-indigo-50 text-indigo-700"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            Completed
          </button>
        </div>
      </div>

      {/* Properties Grid */}
      <div className="mt-6">
        <h2 className="text-lg font-medium text-gray-800 mb-4">
          {filter === "assigned"
            ? "Assigned Properties"
            : filter === "inProgress"
            ? "Properties In Progress"
            : "Completed Properties"}
        </h2>

        {loading ? (
          <div className="bg-white p-6 rounded-lg shadow-sm text-center">
            <p className="text-gray-500">Loading properties...</p>
          </div>
        ) : error ? (
          <div className="bg-white p-6 rounded-lg shadow-sm text-center">
            <p className="text-red-500">{error}</p>
          </div>
        ) : filteredProperties.length === 0 ? (
          <div className="bg-white p-6 rounded-lg shadow-sm text-center">
            <p className="text-gray-500">
              No properties found in this category.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProperties.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                onClick={() => onPropertySelect(property.id)}
                userRole={currentUser ? currentUser.role : "DATA_COLLECTOR"}
              />
            ))}
          </div>
        )}
      </div>

      {/* Data Guidelines */}
      <div className="bg-white p-6 rounded-lg shadow-sm mt-8">
        <h3 className="text-lg font-medium text-gray-800 mb-2">
          Data Collection Guidelines
        </h3>
        <div className="space-y-4 text-sm text-gray-600">
          <p>
            Follow these guidelines to ensure accurate property valuation data:
          </p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Always verify the property boundaries with official UPI
              documentation
            </li>
            <li>Take clear photographs of all sides of the property</li>
            <li>Measure all dimensions accurately and record them promptly</li>
            <li>
              Document all amenities and infrastructure accessible to the
              property
            </li>
            <li>
              Note any special features or defects that may affect valuation
            </li>
          </ol>
          <div className="flex items-center pt-2 text-indigo-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <a href="#" className="font-medium hover:underline">
              Download complete field guidelines
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataCollectorAssignments;
